import moment from 'moment';
import { DeepMap } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import {
  FileInput,
  JobsiteUpdateInput,
} from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { fillJobsiteAddressDetails } from 'components/placesAutocomplete/utils';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import {
  nullifyEmptyFields,
  stringifyEmptyFields,
  tryFileCompression,
} from 'utils';
import { statesOptions, timeZonesOptions } from 'utils/constants';
import { zipCodeValidation } from 'utils/validation';
import {
  BuildingColumnsIcon,
  HandsHoldingChildIcon,
  MapMarkerAltIcon,
} from 'components/icons';
import { acceptImages, dropzoneValidation } from 'components/dropzone/utils';
import { Jobsite } from './types';

export const getJobsiteInfoSectionInputs = ({
  developersOptions,
  jobsitesOptions,
  isTemplate,
}: {
  developersOptions: SelectOptionElement[];
  jobsitesOptions: SelectOptionElement[];
  isTemplate: boolean;
}): TypedFormInputs<EditJobsiteConfigurationFormData['jobsite']> => ({
  profilePictureFile: {
    element: FormInputTypes.Dropzone,
    label: 'Add Profile Picture',
    elementProps: {
      accept: acceptImages,
    },
    validation: {
      validate: dropzoneValidation,
    },
    layout: [GridColSpan.Span6],
  },
  name: {
    element: FormInputTypes.Field,
    label: 'Name',
    validation: { required: true },
    layout: [GridColSpan.SpanFull, isTemplate && GridColSpan.SmSpan2],
  },
  jobsiteNumber: {
    element: FormInputTypes.Field,
    label: 'External Jobsite ID',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  developerId: {
    element: FormInputTypes.Select,
    label: 'Client',
    elementProps: {
      options: developersOptions,
      icon: BuildingColumnsIcon,
    },
    validation: { required: true },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  parentId: {
    element: FormInputTypes.Select,
    label: 'Parent Jobsite',
    elementProps: {
      options: jobsitesOptions,
      icon: HandsHoldingChildIcon,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressLine1: {
    element: FormInputTypes.PlacesAutocomplete,
    label: 'Address',
    elementProps: {
      placeholder: 'Address',
      icon: MapMarkerAltIcon,
      onCommit: fillJobsiteAddressDetails,
    },
    validation: { required: !isTemplate },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressLine2: {
    element: FormInputTypes.Field,
    label: 'Address, Line 2',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressCity: {
    element: FormInputTypes.Field,
    label: 'City',
    validation: { required: !isTemplate },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressState: {
    element: FormInputTypes.Select,
    label: 'State',
    elementProps: {
      options: statesOptions,
    },
    validation: { required: !isTemplate },
    layout: [GridColSpan.Span2, GridColSpan.SmSpan1],
  },
  addressZipCode: {
    element: FormInputTypes.Field,
    label: 'ZIP Code',
    layout: [GridColSpan.Span2, GridColSpan.SmSpan1],
    elementProps: {
      fieldType: 'zipcode',
    },
    validation: {
      required: !isTemplate,
      pattern: zipCodeValidation,
    },
  },
  timeZone: {
    element: FormInputTypes.Select,
    label: 'Timezone',
    elementProps: {
      options: timeZonesOptions,
    },
    validation: { required: !isTemplate },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  latitude: {
    element: FormInputTypes.Field,
    label: 'Latitude',
    elementProps: { fieldType: 'gpsCoordinate' },
    validation: { required: !isTemplate },
    layout: [GridColSpan.Span2, GridColSpan.SmSpan1],
  },
  longitude: {
    element: FormInputTypes.Field,
    label: 'Longitude',
    elementProps: { fieldType: 'gpsCoordinate' },
    validation: { required: !isTemplate },
    layout: [GridColSpan.Span2, GridColSpan.SmSpan1],
  },
  startDate: {
    element: FormInputTypes.DatePicker,
    label: 'Start Date',
    elementProps: {
      showDefaultIcon: true,
      numberOfMonths: 1,
    },
    validation: { required: !isTemplate },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  endDate: {
    element: FormInputTypes.DatePicker,
    label: 'End Date',
    elementProps: {
      showDefaultIcon: true,
      numberOfMonths: 1,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
});

export const getJobsiteInfoSectionDefaultValues = (
  jobsite: Jobsite,
  developersOptions: SelectOptionElement[],
  jobsitesOptions: SelectOptionElement[],
): EditJobsiteConfigurationFormData['jobsite'] => {
  const {
    name,
    jobsiteNumber,
    developer,
    parentId,
    addressLine1,
    addressLine2,
    addressCity,
    addressState,
    addressZipCode,
    timeZone,
    latitude,
    longitude,
    startDate,
    endDate,
    profilePicture,
  } = jobsite ?? {};
  const { developerId } = developer ?? {};

  return {
    ...stringifyEmptyFields({
      name,
      addressLine1,
      addressLine2,
      addressCity,
      addressZipCode,
      jobsiteNumber,
    }),
    profilePictureFile: profilePicture ?? null,
    latitude: latitude?.toString() ?? '',
    longitude: longitude?.toString() ?? '',
    ...nullifyEmptyFields({
      startDate: startDate && moment.utc(startDate),
      endDate: endDate && moment.utc(endDate),
      addressState: statesOptions.find((opt) => opt.value === addressState),
      developerId: developersOptions?.find((opt) => opt.value === developerId),
      parentId: jobsitesOptions?.find((opt) => opt.value === parentId),
      timeZone: timeZonesOptions.find((opt) => opt.value === timeZone),
    }),
  };
};

type JobsiteInfoSectionUpdateInput = Required<
  Pick<
    JobsiteUpdateInput,
    | 'name'
    | 'jobsiteNumber'
    | 'profilePictureFileInput'
    | 'developerId'
    | 'parentId'
    | 'addressLine1'
    | 'addressLine2'
    | 'addressCity'
    | 'addressState'
    | 'addressZipCode'
    | 'timeZone'
    | 'latitude'
    | 'longitude'
    | 'startDate'
    | 'endDate'
  >
>;

export const getJobsiteInfoSectionUpdateInput = async (
  jobsiteInfo: EditJobsiteConfigurationFormData['jobsite'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['jobsite'], true>,
): Promise<JobsiteInfoSectionUpdateInput> => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    jobsiteInfo,
    dirtyFields,
  );
  const getUpdateInputValueAsNumber = (
    field: keyof Pick<
      EditJobsiteConfigurationFormData['jobsite'],
      'latitude' | 'longitude'
    >,
  ): number => {
    const value = getUpdateInputValue(field);
    if (value === undefined) return undefined;
    if (value === null || value === '') return null;
    return Number(value);
  };

  const getFileInput = async (
    field: keyof Pick<
      EditJobsiteConfigurationFormData['jobsite'],
      'profilePictureFile'
    >,
  ): Promise<FileInput> => {
    const value = getUpdateInputValue(field);
    if (value === undefined) return undefined;
    if (value === null) return null;
    const fileData = await tryFileCompression(value as File);
    return { uploadData: fileData, isPublic: false };
  };

  return {
    name: getUpdateInputValue('name'),
    jobsiteNumber: getUpdateInputValue('jobsiteNumber'),
    profilePictureFileInput: await getFileInput('profilePictureFile'),
    developerId: getUpdateInputValue('developerId'),
    parentId: getUpdateInputValue('parentId'),
    addressLine1: getUpdateInputValue('addressLine1'),
    addressLine2: getUpdateInputValue('addressLine2'),
    addressCity: getUpdateInputValue('addressCity'),
    addressState: getUpdateInputValue('addressState'),
    addressZipCode: getUpdateInputValue('addressZipCode'),
    timeZone: getUpdateInputValue('timeZone'),
    latitude: getUpdateInputValueAsNumber('latitude'),
    longitude: getUpdateInputValueAsNumber('longitude'),
    startDate: getUpdateInputValue('startDate'),
    endDate: getUpdateInputValue('endDate'),
  };
};
